import { useTranslation } from "react-i18next";
import { formatDate } from "@core/utils";
import type { PublicMilestone } from "../../types";

/**
 * « Ce qui vous attend » — US-E11-04: when you will hear, and what the programme actually gives.
 *
 * ## Two different kinds of content, deliberately side by side
 *
 * The **timeline** is data: `milestones[]` from the round's own configuration (US-E1-14). The
 * section renders what the round says and nothing else — US-E3-12's core rule is that *the
 * confirmation must not state a timeline written into the screen itself*, and the same applies here,
 * because US-E11-04 requires this page and the confirmation to be the same statement from the same
 * source. A round with no milestones renders no timeline rather than a plausible one.
 *
 * The **what-you-get** list and the funding sentence are editorial. They close two silences the
 * foundation left open (Q4 training dates, Q8 direct funding), and stating the absence honestly is
 * the cheapest self-selection instrument in the module. ⚠️ Wording is provisional pending D10-a —
 * the Figma frame carries the same warning.
 */
export function WhatAwaits({ milestones }: { milestones: PublicMilestone[] }) {
  const { t, i18n } = useTranslation("acquire");

  /**
   * How one milestone states its timing.
   *
   * `EXACT` prints the date the round fixed. `AFTER_CLOSING` prints the interval **in words** —
   * "about 6 weeks after closing" — and never a computed date.
   *
   * That is not laziness. `serialize_milestone` derives `effective_at` using `MONTHS = 30 days`,
   * which is not a calendar month; any client that did its own arithmetic would disagree with the
   * server by days and appear to change the date on save. Rendering the interval sidesteps the
   * whole class of bug, and US-E11-04 asks for the relative form as the DEFAULT anyway: *"a round
   * that opens without confirmed dates is the expected case, not the exception."*
   */
  const timing = (milestone: PublicMilestone): string | null => {
    if (milestone.schedule_type === "EXACT") {
      const at = milestone.exact_at ?? milestone.effective_at;
      return at === null ? null : formatDate(at, i18n.language);
    }
    if (milestone.offset_value === null || milestone.offset_unit === null) {
      return null;
    }
    return t(`preview.awaits.after-closing.${milestone.offset_unit}`, {
      count: milestone.offset_value,
    });
  };

  return (
    <section className="flex flex-col gap-lg border-y border-(--border-default) bg-surface-card px-md pt-lg pb-xl sm:px-xl">
      <div className="flex flex-col gap-xxs">
        <h2 className="text-md-semibold text-(--text-primary)">
          {t("preview.awaits.title")}
        </h2>
        <p className="text-sm-regular text-(--text-secondary)">
          {t("preview.awaits.subtitle")}
        </p>
      </div>

      {milestones.length > 0 ? (
        <>
          <div className="flex flex-col gap-sm">
            <h3 className="text-xs-semibold tracking-wide text-(--text-tertiary) uppercase">
              {t("preview.awaits.after-closing-heading")}
            </h3>
            <ol className="flex flex-col gap-sm">
              {milestones.map((milestone) => {
                const when = timing(milestone);
                return (
                  <li key={milestone.id} className="flex flex-col gap-0.5">
                    {/* The label is the round's, already localized. A round that configured none gets
                      the raw `kind` from the server's own fallback — which is US-E1-14's problem to
                      enforce, not ours to paper over. */}
                    <span className="wrap-break-word text-sm-medium text-(--text-primary)">
                      {milestone.label}
                    </span>
                    {when === null ? null : (
                      <span className="text-xs-regular text-(--text-secondary)">
                        {when}
                      </span>
                    )}
                  </li>
                );
              })}
            </ol>
          </div>

          {/* The rule belongs to the timeline above it. A round with no milestones — which the
              development round genuinely is — would otherwise open the section with a divider
              under nothing. */}
          <div className="h-px w-full bg-(--border-default)" />
        </>
      ) : null}

      {/* ⚠️ Provisional editorial copy — D10-a, Azzedine signs off the register. */}
      <div className="flex flex-col gap-1.5">
        <h3 className="text-xs-semibold tracking-wide text-(--text-tertiary) uppercase">
          {t("preview.awaits.if-selected-heading")}
        </h3>
        <ul className="flex flex-col gap-1.5">
          {/* `as const` so the keys stay literal — `t()` is typed against the locale tree, and a
              widened `string[]` here fails the build rather than silently missing a translation. */}
          {(["training", "mentoring", "network"] as const).map((item) => (
            <li key={item} className="flex items-start gap-2.5">
              <span
                aria-hidden
                className="mt-2 size-1.5 shrink-0 rounded-full bg-(--text-tertiary)"
              />
              <span className="min-w-0 flex-1 wrap-break-word text-sm-regular text-(--text-primary)">
                {t(`preview.awaits.receives.${item}`)}
              </span>
            </li>
          ))}
        </ul>
      </div>

      <div className="h-px w-full bg-(--border-default)" />

      {/*
        Said plainly and at full size — the frame's own note is "jamais en petits caractères".
        « Financement » in the foundation's materials will be read as cash by most applicants, and
        someone applying only for money deserves to know before spending an evening (Q8).
      */}
      <div className="flex flex-col gap-xxs">
        <p className="text-sm-semibold text-(--text-primary)">
          {t("preview.awaits.no-funding-title")}
        </p>
        <p className="text-sm-regular text-(--text-secondary)">
          {t("preview.awaits.no-funding-body")}
        </p>
      </div>
    </section>
  );
}
